package features.timeoff.data

import core.firebase.FirebaseService
import features.timeoff.domain.TimeOffRequest
import kotlin.js.Promise
import kotlin.js.json
import kotlinx.browser.window
import kotlin.js.Date

/**
 * Repository for managing time off requests in Firestore
 */
object TimeOffRepository {
    
    /**
     * Create a new time off request
     * @param teamId Team ID
     * @param userId User ID who is requesting time off
     * @param startDate Start date in YYYY-MM-DD format
     * @param endDate End date in YYYY-MM-DD format
     * @param type Leave category/type
     * @param reason Reason for time off
     * @return Promise with the created request ID
     */
    fun createTimeOffRequest(
        teamId: String,
        userId: String,
        startDate: String,
        endDate: String,
        type: String,
        reason: String
    ): Promise<String> {
        console.log("📝 Creating time off request for user: $userId, dates: $startDate to $endDate")
        
        val days = calculateDays(startDate, endDate)
        val now = Date().toISOString()
        
        val requestData = json(
            "userId" to userId,
            "startDate" to startDate,
            "endDate" to endDate,
            "days" to days,
            "type" to type,
            "reason" to reason,
            "status" to "Pending",
            "createdAt" to now
        )
        
        return FirebaseService.db
            .collection("teams")
            .doc(teamId)
            .collection("timeoff")
            .add(requestData)
            .then { docRef ->
                val requestId = docRef.asDynamic().id as String
                console.log("✅ Time off request created with ID: $requestId")
                requestId
            }
            .catch { error ->
                console.error("❌ Failed to create time off request:", error)
                throw error
            }
    }
    
    /**
     * Get time off requests for a team with optional filters
     * @param teamId Team ID
     * @param userId Optional user ID filter
     * @param status Optional status filter ("Pending", "Approved", "Rejected")
     * @return Promise with list of time off requests
     */
    fun getTimeOffRequests(
        teamId: String,
        userId: String? = null,
        status: String? = null
    ): Promise<List<TimeOffRequest>> {
        console.log("📋 Fetching time off requests for team: $teamId")
        
        var query: dynamic = FirebaseService.db
            .collection("teams")
            .doc(teamId)
            .collection("timeoff")
        
        // Apply filters if provided
        if (userId != null) {
            query = query.where("userId", "==", userId)
        }
        
        if (status != null) {
            query = query.where("status", "==", status)
        }
        
        // Order by creation date (newest first)
        query = query.orderBy("createdAt", "desc")
        
        return query.get()
            .then { snapshot: dynamic ->
                val requests = mutableListOf<TimeOffRequest>()
                
                snapshot.forEach { doc: dynamic ->
                    val rawData: dynamic = doc.data()
                    val request = TimeOffRequest(
                        id = doc.id,
                        userId = rawData.userId as? String ?: "",
                        startDate = rawData.startDate as? String ?: "",
                        endDate = rawData.endDate as? String ?: "",
                        days = (rawData.days as? Double)?.toInt() ?: 0,
                        type = rawData.type as? String ?: "",
                        reason = rawData.reason as? String ?: "",
                        status = rawData.status as? String ?: "Pending",
                        createdAt = formatTimestamp(rawData.createdAt),
                        reviewedAt = if (rawData.reviewedAt != null) formatTimestamp(rawData.reviewedAt) else null,
                        reviewedBy = rawData.reviewedBy as? String
                    )
                    requests.add(request)
                }
                
                console.log("✅ Loaded ${requests.size} time off requests")
                requests
            }
            .catch { error ->
                console.error("❌ Failed to fetch time off requests:", error)
                throw error
            }
    }
    
    /**
     * Get a single time off request by ID
     * @param teamId Team ID
     * @param requestId Request ID
     * @return Promise with the time off request or null if not found
     */
    fun getTimeOffRequestById(teamId: String, requestId: String): Promise<TimeOffRequest?> {
        console.log("🔍 Fetching time off request: $requestId")
        
        return FirebaseService.db
            .collection("teams")
            .doc(teamId)
            .collection("timeoff")
            .doc(requestId)
            .get()
            .then { doc: dynamic ->
                if (doc.exists) {
                    val rawData: dynamic = doc.data()
                    TimeOffRequest(
                        id = doc.id,
                        userId = rawData.userId as? String ?: "",
                        startDate = rawData.startDate as? String ?: "",
                        endDate = rawData.endDate as? String ?: "",
                        days = (rawData.days as? Double)?.toInt() ?: 0,
                        type = rawData.type as? String ?: "",
                        reason = rawData.reason as? String ?: "",
                        status = rawData.status as? String ?: "Pending",
                        createdAt = formatTimestamp(rawData.createdAt),
                        reviewedAt = if (rawData.reviewedAt != null) formatTimestamp(rawData.reviewedAt) else null,
                        reviewedBy = rawData.reviewedBy as? String
                    )
                } else {
                    console.warn("⚠️ Time off request not found: $requestId")
                    null
                }
            }
            .catch { error ->
                console.error("❌ Failed to fetch time off request:", error)
                throw error
            }
    }
    
    /**
     * Approve a time off request
     * @param teamId Team ID
     * @param requestId Request ID
     * @param reviewerId User ID of the reviewer (admin/owner)
     * @return Promise
     */
    fun approveRequest(teamId: String, requestId: String, reviewerId: String): Promise<Unit> {
        console.log("✅ Approving time off request: $requestId by reviewer: $reviewerId")
        
        val now = Date().toISOString()
        val updateData = json(
            "status" to "Approved",
            "reviewedAt" to now,
            "reviewedBy" to reviewerId
        )
        
        return FirebaseService.db
            .collection("teams")
            .doc(teamId)
            .collection("timeoff")
            .doc(requestId)
            .update(updateData)
            .then {
                console.log("✅ Time off request approved successfully")
                Unit
            }
            .catch { error ->
                console.error("❌ Failed to approve time off request:", error)
                throw error
            }
    }
    
    /**
     * Reject a time off request
     * @param teamId Team ID
     * @param requestId Request ID
     * @param reviewerId User ID of the reviewer (admin/owner)
     * @return Promise
     */
    fun rejectRequest(teamId: String, requestId: String, reviewerId: String): Promise<Unit> {
        console.log("❌ Rejecting time off request: $requestId by reviewer: $reviewerId")
        
        val now = Date().toISOString()
        val updateData = json(
            "status" to "Rejected",
            "reviewedAt" to now,
            "reviewedBy" to reviewerId
        )
        
        return FirebaseService.db
            .collection("teams")
            .doc(teamId)
            .collection("timeoff")
            .doc(requestId)
            .update(updateData)
            .then {
                console.log("✅ Time off request rejected successfully")
                Unit
            }
            .catch { error ->
                console.error("❌ Failed to reject time off request:", error)
                throw error
            }
    }
    
    /**
     * Calculate number of days between start and end date (inclusive)
     * @param startDate Start date in YYYY-MM-DD format
     * @param endDate End date in YYYY-MM-DD format
     * @return Number of days
     */
    fun calculateDays(startDate: String, endDate: String): Int {
        val start = Date(startDate)
        val end = Date(endDate)
        
        val diffMs = end.getTime() - start.getTime()
        val diffDays = (diffMs / (1000 * 60 * 60 * 24)).toInt()
        
        // Add 1 to include both start and end dates
        return diffDays + 1
    }
    
    /**
     * Format date range for display
     * @param startDate Start date in YYYY-MM-DD format
     * @param endDate End date in YYYY-MM-DD format
     * @return Formatted string like "Feb 10 - Feb 14, 2026"
     */
    fun formatDateRange(startDate: String, endDate: String): String {
        val start = Date(startDate)
        val end = Date(endDate)
        
        val months = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
        
        val startMonth = months[start.getMonth()]
        val startDay = start.getDate()
        val endMonth = months[end.getMonth()]
        val endDay = end.getDate()
        val year = end.getFullYear()
        
        return if (startMonth == endMonth) {
            // Same month: "Feb 10 - 14, 2026"
            "$startMonth $startDay - $endDay, $year"
        } else {
            // Different months: "Feb 28 - Mar 3, 2026"
            "$startMonth $startDay - $endMonth $endDay, $year"
        }
    }
    
    /**
     * Format Firestore timestamp to string
     * @param timestamp Firestore timestamp
     * @return Formatted date string
     */
    private fun formatTimestamp(timestamp: dynamic): String {
        return try {
            if (timestamp == null) {
                Date().toISOString()
            } else if (timestamp.toDate != null) {
                // Firestore Timestamp object
                val date = timestamp.toDate() as Date
                date.toISOString()
            } else {
                // Already a string
                timestamp as String
            }
        } catch (e: Exception) {
            console.error("Failed to format timestamp:", e)
            Date().toISOString()
        }
    }
}
